import { useMemo, useCallback, useEffect } from 'react';
import { Col, Row, Form, Tooltip } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useNavigate } from 'hooks/useNavigate';
import { useParams } from 'react-router-dom';

import VacancyApplicationTypesApi from 'libs/api/cv/vacancyApplicationTypes';
import { useApi } from 'hooks/useApi';

const api = new VacancyApplicationTypesApi();

/**
 * 
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{
 *  columns: any,
 *  data: any,
 *  singleItem: any,
 *  loading: boolean,
 *  updateInProcess: boolean,
 *  setUpdateInProcess: func,
 *  onDelete: func,
 *  onChangeTableFilter: func
 * }} state and callbacks for ExperienceYears page
 */

const useVacancyApplicationTypes = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pages = useRecoilValue(getPage);
  const [ form ] = Form.useForm();
  const { id } = useParams();

  const { loading, data, singleItem, onDelete, onChangeTableFilter, updateInProcess, setUpdateInProcess } = useApi({
    resourceKey: 'vacancy_application_types',
    singleResourceKey: 'vacancy_application_type',
    api,
    isMainPage,
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        dataIndex: 'title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 250,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            {(pages?.ability?.can_read !== false ||
              row.can_read !== false) && (
              <Col>
                <Tooltip title={t('Visualizza')}>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type='secondary' icon={<EyeOutlined />} />
                  </Link>
                </Tooltip>
              </Col>
            )}
            {row.can_update !== false && (
              <Col>
                <Tooltip title={t('Modifica')}>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Tooltip>
              </Col>
            )}
            {row.can_destroy !== false && (
              <Col>
                <Tooltip title={t('Elimina')}>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Tooltip>
              </Col>
            )}
          </Row>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, pages?.ability?.can_read, t]);

  const handleCreateOrUpdate = useCallback(async ({ title }) => {
    setUpdateInProcess(true);
    
    const data = {
      vacancy_application_type: {
        title
      }
    };

    try {
      if (!id) {
        await api.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, { data });
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors),
      );
    } finally {
      setUpdateInProcess(false);
    }
  }, [setUpdateInProcess, form, navigate, pathname, id]);

  useEffect(() => {
    if (id && !isMainPage && singleItem?.title) {
      form.setFields([{
        name: 'title',
        value: singleItem.title
      }]);
    }
  }, [singleItem, form, id, isMainPage]);

  return {
    form,
    columns,
    data,
    singleItem,
    loading,
    onDelete,
    updateInProcess,
    handleCreateOrUpdate,
    onChangeTableFilter,
  };
};

export default useVacancyApplicationTypes;
